import GoalsImg from '@assets/images/onboarding/goals.svg';
import IndustryImg from '@assets/images/onboarding/industry.svg';
import IntroductionImg from '@assets/images/onboarding/introduction.svg';
import ProfileImg from '@assets/images/onboarding/profile.svg';
import { Loader, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// import { useIndicatorProductSubscribe } from '@/api/rest/resources/indicatorProduct';
import { RoleEnum } from '@/api/rest/resources/types/membership';
import { BaseContainer, GuidedContainer, GuidedContainerFooterProps, SplitView } from '@/components/Journey/SplitView';
import { useIsOrganizationOnboarded } from '@/hooks/useIsOrganizationOnboarded';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { usePathSet } from '@/hooks/usePathSet';
import { useUser } from '@/lib/auth';
import { buildPath } from '@/utils/buildPath';

import { useUserOrganizationId } from '../../hooks/useUserOrganizationId';
import { OnboardingForm, useOnboardingFormContext } from './components/form/OnboardingForm';
import { OnboadingIndustry } from './components/OnboadingIndustry';
import { OnboardingGoals } from './components/OnboardingGoals';
import { OnboardingGoalsCompliance } from './components/OnboardingGoalsCompliance';
import { OnboardingGoalsContext } from './components/OnboardingGoalsContext';
import {
  // OnboardingIndicatorProducts,
  SelectedIndicatorsProvider,
  // useSelectedIndicatorsContext,
} from './components/OnboardingIndicatorProducts';
import { OnboardingIntroduction } from './components/OnboardingIntroduction';
import { OnboardingProfile } from './components/OnboardingProfile';
// TODO: Uncomment when UI ability to upload plots is fixed
/* import { OnboardingUploadPlots } from './components/OnboardingUploadPlots'; */

const FORM_PAGES = {
  pageOne: '1',
  pageTwo: '2',
  pageThree: '3',
  pageFour: '4',
  pageFive: '5',
  pageSix: '6',
  // pageSeven: '7',
  // pageEight: '8',
} as const;

export const ONBOARDING_FORM_PAGES = [
  FORM_PAGES.pageOne,
  FORM_PAGES.pageTwo,
  FORM_PAGES.pageThree,
  FORM_PAGES.pageFour,
  FORM_PAGES.pageFive,
  FORM_PAGES.pageSix,
];

export const OrganizationOnboardingPage = () => {
  return (
    <OnboardingForm>
      <SelectedIndicatorsProvider>
        <OnboardingSteps />
      </SelectedIndicatorsProvider>
    </OnboardingForm>
  );
};

const OnboardingSteps = () => {
  const { t } = useTranslation();
  const { onboardingSection } = useParams();
  const pathSet = usePathSet();
  const organizationId = useUserOrganizationId();
  const navigate = useNavigate();

  const isStaff = useIsStaffUser();
  const user = useUser();
  const isAdmin = user.membership.role === RoleEnum.admin;
  const isEditor = user.membership.role === RoleEnum.editor;

  // const { selectedIndicators } = useSelectedIndicatorsContext();

  const isOrganizationOnboarded = useIsOrganizationOnboarded();

  // const { mutate, isPending } = useIndicatorProductSubscribe();

  const pageOneInt = parseInt(FORM_PAGES.pageOne, 10);
  const maxPageInt = Math.max(...Object.values(FORM_PAGES).map((page) => parseInt(page, 10)));
  const onBoardingSectionNumber = onboardingSection ? parseInt(onboardingSection, 10) : pageOneInt;

  const { watch } = useOnboardingFormContext();
  const values = watch();

  const isValidInputsForSection = (section: string) => {
    switch (section) {
      case FORM_PAGES.pageTwo:
        return values.profile;
      case FORM_PAGES.pageThree:
        return values.industry;
      // case FORM_PAGES.pageSeven:
      //   return selectedIndicators.length > 0;
      default:
        return true;
    }
  };

  const progressProps = {
    currentStep: onBoardingSectionNumber,
    totalSteps: isEditor ? 7 : Object.keys(FORM_PAGES).length,
    className: 'w-48 min-w-[200px]',
  };

  const pageOnePreviousPath = isOrganizationOnboarded
    ? pathSet.copilot
    : buildPath(pathSet.organizationOnboarding, {
        pathParams: {
          organizationId,
          onboardingSection: pageOneInt,
        },
      });

  const previousUrl =
    onBoardingSectionNumber !== pageOneInt
      ? buildPath(pathSet.organizationOnboarding, {
          pathParams: {
            organizationId,
            onboardingSection: onBoardingSectionNumber - 1,
          },
        })
      : pageOnePreviousPath;

  const handleNextAction = (e?: Event) => {
    e?.preventDefault();
    e?.stopPropagation();

    const nextUrl = buildPath(pathSet.organizationOnboarding, {
      pathParams: {
        organizationId,
        onboardingSection:
          onBoardingSectionNumber + 1 > maxPageInt ? onBoardingSectionNumber : onBoardingSectionNumber + 1,
      },
    });

    navigate(nextUrl, {
      state: { from: previousUrl },
    });
  };

  // const handleSelectIndicatorProducts = () => {
  //   mutate(
  //     {
  //       bodyData: {
  //         organization_id: organizationId,
  //         indicator_product_ids: selectedIndicators,
  //       },
  //     },
  //     {
  //       onSuccess: () => {
  //         // handleNextAction();
  //         navigate(pathSet.home);
  //       },
  //       onError: (error) => {
  //         toast({
  //           title: t('global.ui.toast.errorToastFallbackTitle'),
  //           description: error.message,
  //           type: 'error',
  //         });
  //       },
  //     },
  //   );
  // };
  const footer: GuidedContainerFooterProps = {
    nextAction: handleNextAction,
    backPath: previousUrl,
    showFooter: true,
    disableBackAction: onBoardingSectionNumber === pageOneInt && !isOrganizationOnboarded,
    disableNextAction: !isValidInputsForSection(onboardingSection || ''),
  };

  const formLastPage = Math.max(...ONBOARDING_FORM_PAGES.map((page) => parseInt(page, 10)));

  // if (String(onBoardingSectionNumber) === String(FORM_PAGES.pageSeven)) {
  //   footer.nextAction = handleSelectIndicatorProducts;
  // } else
  if (String(onBoardingSectionNumber) === String(formLastPage)) {
    if (isAdmin || isStaff) {
      footer.nextActionType = 'submit' as const;
      footer.nextAction = undefined;
    } else {
      footer.nextAction = () => navigate(pathSet.home);
    }
  }

  /* if (String(onBoardingSectionNumber) === String(FORM_PAGES.pageEight)) {
* footer.nextText = 'shared.onboarding.plotsUpload.skipButton';
* footer.nextVariant = 'text';
* footer.nextAction = () => navigate(pathSet.home);
* footer.disableBackAction = true;
  } */

  /* const isSectionWithoutImage =
   *   onboardingSection && [FORM_PAGES.pageFive, FORM_PAGES.pageSix].includes(onboardingSection as '5' | '6'); */

  // const isSectionWithoutImage = onboardingSection && [FORM_PAGES.pageSeven].includes(onboardingSection as '7');
  const isPending = false;
  const isSectionWithoutImage = false;

  return (
    <SplitView className={isSectionWithoutImage ? 'grid-cols-1' : ''}>
      <GuidedContainer className='px-20 py-16 h-screen' progressProps={progressProps} footer={footer}>
        {isPending ? (
          <Stack className='h-full' center centerMain spacing={4}>
            <div>
              <Loader className='min-h-fit' />
            </div>
            <span className='typography-display4'>{t('shared.onboarding.processingData')}</span>
          </Stack>
        ) : (
          <Section />
        )}
      </GuidedContainer>
      {!isSectionWithoutImage && (
        <BaseContainer>
          <Image />
        </BaseContainer>
      )}
    </SplitView>
  );
};

const Section = () => {
  const { onboardingSection } = useParams();

  switch (onboardingSection) {
    case FORM_PAGES.pageOne:
      return <OnboardingIntroduction />;
    case FORM_PAGES.pageTwo:
      return <OnboardingProfile />;
    case FORM_PAGES.pageThree:
      return <OnboadingIndustry />;
    case FORM_PAGES.pageFour:
      return <OnboardingGoals />;
    case FORM_PAGES.pageFive:
      return <OnboardingGoalsCompliance />;
    case FORM_PAGES.pageSix:
      return <OnboardingGoalsContext />;
    // case FORM_PAGES.pageSeven:
    //   return <OnboardingIndicatorProducts />;
    /* case FORM_PAGES.pageEight:
     *   return <OnboardingUploadPlots />; */
    default:
      return <OnboardingIntroduction />;
  }
};

const Image = () => {
  const { onboardingSection } = useParams();

  switch (onboardingSection) {
    case FORM_PAGES.pageOne:
      return <ImageWrapper imgSrc={IntroductionImg} />;
    case FORM_PAGES.pageTwo:
      return <ImageWrapper imgSrc={ProfileImg} />;
    case FORM_PAGES.pageThree:
      return <ImageWrapper imgSrc={IndustryImg} />;
    case FORM_PAGES.pageFour:
    case FORM_PAGES.pageFive:
    case FORM_PAGES.pageSix:
      return <ImageWrapper imgSrc={GoalsImg} />;
    // case FORM_PAGES.pageSeven:
    //   return null;
    /* case FORM_PAGES.pageEight:
     *   return null; */
    default:
      return <ImageWrapper imgSrc={IntroductionImg} />;
  }
};

const ImageWrapper = ({ imgSrc }: { imgSrc: string }) => {
  return <img className='w-full h-full' alt='onboarding-progress-visualization' src={imgSrc as string} />;
};
